import React, { useState } from 'react';
import { Alert, Button, Card, Form, Input, InputNumber, Select, Slider, Typography } from 'antd';
import { useHistory } from 'react-router-dom';
import { calculateCost, calculateCostPerMonth, humanFileSize } from './utils';
import { AddServerPayload } from './NewServer';
import ServerPricingInfo from './ServerPricingInfo';

interface FormValues {
	appname: string;
	size: number;
	size_gb: number;
	url_name: string;
	url_domain: string;
	location: string;
}

const MIN_SERVER_SIZE = 1 * 1024 * 1024 * 1024;
const MAX_SERVER_SIZE = 20 * 1024 * 1024 * 1024 * 1024;
const SERVER_SIZE_STEP = 1 * 1024 * 1024 * 1024;
const DEFAULT_SERVER_SIZE = 10 * 1024 * 1024 * 1024;
export const BYTES_PER_GB = 1024 * 1024 * 1024;
const MAX_SERVER_SIZE_GB = MAX_SERVER_SIZE / BYTES_PER_GB;
export const INITIAL_SERVER_SETUP_STORAGE_KEY = 'initial_server_setup';
export const INITIAL_SERVER_SETUP_SIZE_STORAGE_KEY = 'initial_server_setup_size';

const NewServerStart: React.FC = () => {
	const history = useHistory();
	const [submitting, setSubmitting] = useState<boolean>(false);
	const [statusText, setStatusText] = useState<string>('');
	const [errorText, setErrorText] = useState<string>('');
	const [form] = Form.useForm();

	const handleSubmit = async (values: any) => {
		const formValues = values as FormValues;
		const payload: AddServerPayload = {
			appname: formValues.appname,
			size: formValues.size,
			location: formValues.location,
			url_name: formValues.url_name,
			url_domain: formValues.url_domain,
		};

		setSubmitting(true);
		setStatusText('Saving server setup information...');
		setErrorText('');

		try {
			const formData = new FormData();
			formData.append('data', JSON.stringify(payload));

			const resp = await fetch('/api/save_server', {
				method: 'POST',
				credentials: 'include',
				body: formData,
			});
			const data = await resp.json();

			if (!data.ok || !data.uuid) {
				setSubmitting(false);
				setErrorText(data.err || data.error || 'Could not save server setup information');
				setStatusText('');
				return;
			}

			localStorage.setItem(INITIAL_SERVER_SETUP_SIZE_STORAGE_KEY, payload.size.toString());
			localStorage.setItem(INITIAL_SERVER_SETUP_STORAGE_KEY, data.uuid);
			setStatusText('Server setup information saved. The server will be created after payment.');
			history.push('/app/register');
		} catch (error) {
			setSubmitting(false);
			setStatusText('');
			setErrorText('Connection issue while saving server setup information');
		}
	};

	const handleSliderChange = (value: number | [number, number]) => {
		if (Array.isArray(value)) {
			return;
		}

		form.setFieldsValue({
			size: value,
			size_gb: Math.round(value / BYTES_PER_GB),
		});
	};

	const handleGbChange = (value: number | undefined) => {
		if (typeof value === 'undefined') {
			return;
		}

		const clampedGb = Math.max(1, Math.min(MAX_SERVER_SIZE_GB, Math.round(value)));
		form.setFieldsValue({
			size_gb: clampedGb,
			size: clampedGb * BYTES_PER_GB,
		});
	};

	return (
		<div
			style={{
				minHeight: '100vh',
				padding: '32px 20px',
				background:
					'radial-gradient(circle at top left, rgba(14, 165, 233, 0.22), transparent 28%), radial-gradient(circle at top right, rgba(34, 197, 94, 0.15), transparent 24%), linear-gradient(180deg, #07131d 0%, #102030 40%, #f5f7fb 40%, #f5f7fb 100%)'
			}}
		>
			<div style={{ maxWidth: 1200, margin: '0 auto' }}>
				<div style={{ marginBottom: 24, color: '#eaf3fb' }}>
					<Typography.Text style={{ color: 'rgba(234, 243, 251, 0.7)', textTransform: 'uppercase', letterSpacing: 1.4 }}>
						Server setup
					</Typography.Text>
					<Typography.Title level={1} style={{ color: '#ffffff', marginTop: 8, marginBottom: 8 }}>
						Set up your first server
					</Typography.Title>
					<Typography.Paragraph
						style={{ color: 'rgba(234, 243, 251, 0.82)', maxWidth: 760, fontSize: 16, lineHeight: 1.8, marginBottom: 0 }}
					>
						Choose the server name, storage size, and location for the first environment. Pricing updates live as you adjust the storage size.
					</Typography.Paragraph>
				</div>

				<div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.1fr) minmax(320px, 0.9fr)', gap: 24, alignItems: 'start' }}>
					<Card
						style={{ borderRadius: 20, boxShadow: '0 24px 80px rgba(3, 11, 21, 0.12)' }}
						title={<Typography.Title level={3} style={{ margin: 0 }}>Server details</Typography.Title>}
					>
						<div style={{ display: 'grid', gap: 16 }}>
							{statusText.length > 0 && <Alert message={statusText} type="info" showIcon />}
							{errorText.length > 0 && <Alert message={errorText} type="error" showIcon />}

							<Form
								form={form}
								layout="vertical"
								onFinish={handleSubmit}
								initialValues={{
									location: 'Germany',
									url_domain: 'urbackup.app',
									size: DEFAULT_SERVER_SIZE,
									size_gb: DEFAULT_SERVER_SIZE / BYTES_PER_GB,
								}}
							>
								<div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
									<Form.Item
										name="appname"
										label="App name"
										rules={[
											{ required: true, message: 'Please enter an app name' },
											{ min: 1, max: 63, message: 'App name must be 1 to 63 characters' },
											{ pattern: /^[A-Za-z0-9-]+$/, message: 'Allowed characters: A-Z, a-z, 0-9 and -' },
										]}
									>
										<Input placeholder="Example: Testserver10" disabled={submitting} />
									</Form.Item>

									<Form.Item
										name="url_name"
										label="URL name"
										rules={[{ required: true, message: 'Please enter a URL name' }]}
									>
										<Input placeholder="Example: testserver49" disabled={submitting} />
									</Form.Item>
								</div>

								<Form.Item
									name="size"
									label="Storage size"
									rules={[{ required: true, message: 'Please select a storage size' }]}
								>
									<Slider
										disabled={submitting}
										min={MIN_SERVER_SIZE}
										max={MAX_SERVER_SIZE}
										step={SERVER_SIZE_STEP}
										onChange={handleSliderChange}
										marks={{
											[MIN_SERVER_SIZE]: '1 GiB',
											[DEFAULT_SERVER_SIZE]: '10 GiB',
											[MAX_SERVER_SIZE]: '20 TiB',
										}}
									/>
								</Form.Item>

								<div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 16 }}>
									<Form.Item
										name="size_gb"
										label="Storage size (GB)"
										rules={[{ required: true, message: 'Please enter a storage size in GB' }]}
									>
										<InputNumber
											disabled={submitting}
											min={1}
											max={MAX_SERVER_SIZE_GB}
											step={1}
											precision={0}
											style={{ width: '100%' }}
											onChange={handleGbChange}
										/>
									</Form.Item>

									<Form.Item name="url_domain" label="URL domain" rules={[{ required: true, message: 'Please select a URL domain' }]}>
										<Select disabled={submitting}>
											<Select.Option value="urbackup.app">urbackup.app</Select.Option>
										</Select>
									</Form.Item>
								</div>

								<Form.Item name="location" label="Location" rules={[{ required: true, message: 'Please select a location' }]}>
									<Select disabled={submitting}>
										<Select.Option value="Germany">Germany</Select.Option>
									</Select>
								</Form.Item>

								<Form.Item noStyle shouldUpdate>
									{() => {
										const currentSize = Number(form.getFieldValue('size') || DEFAULT_SERVER_SIZE);
										return (
											<div style={{ marginTop: -8, marginBottom: 8, color: '#c7c7c7' }}>
												Selected storage: {humanFileSize(currentSize, false)}
											</div>
										);
									}}
								</Form.Item>

								<Form.Item noStyle shouldUpdate>
									{() => {
										const currentSizeGb = Number(form.getFieldValue('size_gb') || DEFAULT_SERVER_SIZE / BYTES_PER_GB);
										return <ServerPricingInfo sizeGb={currentSizeGb} />;
									}}
								</Form.Item>

								<Form.Item style={{ marginBottom: 0 }}>
									<Button type="primary" htmlType="submit" loading={submitting} block size="large">
										Save and continue to account setup
									</Button>
								</Form.Item>
							</Form>
						</div>
					</Card>

					<Card
						style={{ borderRadius: 20, boxShadow: '0 24px 80px rgba(3, 11, 21, 0.10)', background: 'linear-gradient(180deg, #0f1720 0%, #152433 100%)', color: '#f4f7fb' }}
						bodyStyle={{ padding: 28 }}
					>
						<Typography.Text style={{ color: 'rgba(244, 247, 251, 0.65)', textTransform: 'uppercase', letterSpacing: 1.2 }}>
							Setup summary
						</Typography.Text>
						<Typography.Title level={3} style={{ color: '#ffffff', marginTop: 8 }}>
							A server profile you can change later.
						</Typography.Title>
						<Typography.Paragraph style={{ color: 'rgba(244, 247, 251, 0.8)', lineHeight: 1.8, fontSize: 15 }}>
							Pick a size, choose a location, and we’ll create the account and environment after checkout.
						</Typography.Paragraph>

						<div style={{ display: 'grid', gap: 14, marginTop: 20 }}>
							<div style={{ padding: 16, borderRadius: 14, background: 'rgba(255, 255, 255, 0.06)' }}>
								<Typography.Text style={{ color: '#8bd3ff', display: 'block', marginBottom: 4 }}>1. Define</Typography.Text>
								<Typography.Text style={{ color: '#ffffff' }}>Choose the app name and URL identity.</Typography.Text>
							</div>
							<div style={{ padding: 16, borderRadius: 14, background: 'rgba(255, 255, 255, 0.06)' }}>
								<Typography.Text style={{ color: '#8bd3ff', display: 'block', marginBottom: 4 }}>2. Size</Typography.Text>
								<Typography.Text style={{ color: '#ffffff' }}>Select the storage size that fits the first deployment.</Typography.Text>
							</div>
							<div style={{ padding: 16, borderRadius: 14, background: 'rgba(255, 255, 255, 0.06)' }}>
								<Typography.Text style={{ color: '#8bd3ff', display: 'block', marginBottom: 4 }}>3. Continue</Typography.Text>
								<Typography.Text style={{ color: '#ffffff' }}>We’ll save the setup and move to account creation.</Typography.Text>
							</div>
						</div>
					</Card>
				</div>
			</div>
		</div>
	);
};

export default NewServerStart;
